import React, {ReactNode, useEffect, useMemo, useRef, useState} from 'react'
import * as d3 from 'd3'
import moment from 'moment'
import {Card, Colors, P} from '../UI/index.js'
import prefersReducedMotion from '../../utils/PrefersReducedMotion.js'
import {NumberValue} from 'd3'

export interface VerticalBarChartStackedProps {
    width: number
    height: number
    data: {
        values: {
            value: number
            key: string
            href?: string
            valueLabel?: string | number
        }[]
        date: string
    }[]
    colorMap: {
        [key: string]: string
    }
    maximumY?: number
    dataSuffix?: string
    showGrid?: boolean
    animated?: boolean
    interval?: 'day' | 'week' | 'month'
    tooltipHrefText?: string
    history?: any
}

export default function VerticalBarChartStacked({width, height, data, colorMap, maximumY, dataSuffix, animated = !prefersReducedMotion, interval = 'day', tooltipHrefText, history}: VerticalBarChartStackedProps) {
    const [currentStack, setCurrentStack] = useState<VerticalBarChartStackedProps['data'][0]['values'][0] & {date: string}>()
    // const [persistPopup, setPersistPopup] = useState<boolean>(false)
    const tooltipRef = useRef<HTMLDivElement>(null)
    const margin = {top: 40, right: 40, bottom: 60, left: 100}
    const barSpacing = 10

    const svgRef = useRef(null)

    const dataIsEmpty = useMemo(() => data.length === 0, [data])

    const minimumX = interval === 'month' ? d3.min(data, (d) => d.date) || 0 : d3.min(data, (d) => new Date(d.date)) || 0
    let maximumX = interval === 'month' ? d3.max(data, (d) => d.date) || 0 : d3.max(data, (d) => new Date(d.date)) || 0

    let tickInterval = {
        day: d3.utcDay,
        week: d3.utcWeek,
        month: d3.utcMonth
    }[interval]

    const minAmount = 7
    const intervalCount = tickInterval.count(minimumX as Date, maximumX as Date)
    if (intervalCount < minAmount) {
        const newMax = moment(minimumX as Date).add(minAmount - 1, interval)
        maximumX = newMax.toDate()
    }

    const barWidth = useMemo(() => {
        const length = interval === 'month' ? data.length : tickInterval.count(minimumX as Date, maximumX as Date) + 1
        const newWidth = (width - margin.left - margin.right) / length - barSpacing
        if (newWidth > 300) {
            return 300
        }

        if (newWidth < 10) {
            return 10
        }

        return newWidth
    }, [data, width, interval])

    const xScale = useMemo(() => {
        if (interval === 'month') {
            return d3.scaleBand()
                .domain(data.map((d) => d.date))
                .range([margin.left, width - margin.right])
        } else {
            return d3.scaleUtc()
                .domain([minimumX as Date, maximumX as Date])
                .range([margin.left, width - margin.right - barWidth])
        }
    }, [data, width, interval, barWidth])

    useEffect(() => {
        if (svgRef.current) {
            const svg = d3.select(svgRef.current)
            const count = svg.selectAll('.stacked-bar').size()
            const duration = 750
            const delay = 50
            const totalDur = Math.min((duration + (count - 1) * delay), 1_000) // max duration of 1 second
            const bars = svg.selectAll('.bar')
            // const labels = bars.select((_, i, bars) => bars[i].nextElementSibling)

            if (animated) {
                const delayDurPerBar = totalDur / bars.size()

                // labels.attr('x', (_, i, labels) => {
                //     const el = labels[i]
                //     const value = el.getAttribute('data-value')

                //     const currentX = el.getAttribute('x')

                //     if (currentX) {
                //         return currentX
                //     }

                //     return xScale(value) - xScale(minimumX)
                // })

                bars
                    .attr('height', 0)
                    .attr('y', yScale(0))
                    .transition()
                    .duration(totalDur)
                    .delay((_, i) => i * delayDurPerBar)
                    .attr('height', (_, i, bars) => {
                        const el = bars[i] as Element
                        if (!el) {
                            return null
                        }

                        let start, end: string | number | null
                        [start, end] = [el.getAttribute('data-start'), el.getAttribute('data-end')]
                        if (!start || !end) {
                            return null
                        }

                        start = parseFloat(start)
                        end = parseFloat(end)

                        if (isNaN(start) || isNaN(end)) {
                            return null
                        }

                        return yScale(start) - yScale(end)
                    })
                    .attr('y', (_, i, bars) => {
                        const el = bars[i] as Element
                        if (!el) {
                            return null
                        }

                        const endValue = el.getAttribute('data-end')
                        if (!endValue) {
                            return null
                        }

                        const end = parseFloat(endValue)
                        if (isNaN(end)) {
                            return null
                        }

                        return yScale(end)
                    })

                let persistPopup = false

                bars
                    .on('mouseover', (event) => {
                        const el = event.currentTarget
                        const elDate = el.getAttribute('data-date')
                        const key = el.getAttribute('data-key')
                        const currentStack = data.find((d) => d.date === elDate)?.values.find((v) => v.key === key)
                        if (currentStack) {
                            setCurrentStack((prevStack) => {
                                if (prevStack?.key === currentStack.key && prevStack?.date === elDate) {
                                    return prevStack
                                }
                                persistPopup = false
                                return {...currentStack, date: elDate}
                            })
                            tooltipRef.current!.style.visibility = 'visible'
                        }
                    })
                    .on('mousemove', (event) => {
                        if (!persistPopup) {
                            tooltipRef.current!.style.top = `${event.offsetY + 10}px`
                            tooltipRef.current!.style.left = `${event.offsetX + 10}px`
                        }
                    })
                    .on('mouseout', () => {
                        if (!persistPopup) {
                            tooltipRef.current!.style.visibility = 'hidden'
                        }
                    })
                    // .on('click', (event) => {
                    //     const el = event.currentTarget
                    //     const elDate = el.getAttribute('data-date')
                    //     const key = el.getAttribute('data-key')
                    //     const currentStack = data.find((d) => d.date === elDate)?.values.find((v) => v.key === key)
                    //     if (currentStack) {
                    //         setCurrentStack({...currentStack, date: elDate})
                    //         tooltipRef.current!.style.visibility = 'visible'
                    //         setPersistPopup(true)
                    //     }
                    // })
                    .on('contextmenu', (event) => {
                        event.preventDefault()
                        if (persistPopup) {
                            tooltipRef.current!.style.top = `${event.pageY}px`
                            tooltipRef.current!.style.left = `${event.pageX}px`
                            // tooltipRef.current!.style.visibility = 'hidden'
                        }
                        persistPopup = !persistPopup
                    })

                d3.select(tooltipRef.current).on('mouseleave', () => {
                    tooltipRef.current!.style.visibility = 'hidden'
                })
            }

            // svg.call(zoom)
        }
    }, [data, height])

    const series = data.map((d) => {
        const stacks: {
            key: string
            start: number
            end: number
        }[] = []

        let currentValue = 0
        d.values.forEach((v) => {
            stacks.push({
                key: v.key,
                start: currentValue,
                end: currentValue + v.value
            })

            currentValue += v.value
        })

        return {
            date: d.date,
            stacks,
            total: currentValue
        }
    })

    const yScale = useMemo(() => {
        return d3.scaleLinear()
            .domain([0, dataIsEmpty ? 100 : maximumY || d3.max(series, (serie) => serie.total) || 0]).nice()
            .range([height - margin.bottom, margin.top])
    }, [data, height])

    const maxTicks = Math.floor((width - margin.left - margin.right) / 20)

    while (tickInterval.count(minimumX as Date, d3.max(data, (d) => new Date(d.date)) as Date) > maxTicks) {
        if (interval === 'day') {
            tickInterval = d3.utcWeek
        } else if (interval === 'week') {
            tickInterval = d3.utcMonth
        } else {
            tickInterval = d3.utcMonth
        }
    }

    const xTicks = interval === 'month' ?
        data.map((d) => d.date) :
        (xScale as d3.ScaleTime<number, number, never>).ticks(tickInterval.every(1)!)

    const xTickFormat = {
        day: 'DD-MM',
        week: '[WK]W',
        month: 'MMM'
    }[interval]

    const xTickElements = xTicks.map((tick: string | Date) => (
        <g key={typeof tick === 'string' ? tick : tick.getTime()} transform={`translate(${xScale(tick as string & (Date | NumberValue)) + barWidth / 2}, ${height})`}>
            <text style={{textAnchor: 'middle', fontSize: 16, transform: 'rotate(-65deg) translate(20px, 0px)'}}>{moment(tick).format(xTickFormat)}</text>
        </g>
    ))

    const [min, max] = yScale.domain()

    const step = Math.max(1, Math.ceil((max - min) / 5))

    const yTicks = d3.range(
        Math.ceil(min),
        Math.floor(max) + 1,
        step
    )

    // const yTicks = yScale.ticks()

    const yTickElements = yTicks.map((tick) => (
        <g key={tick} transform={`translate(0, ${yScale(tick)})`}>
            <text x={xScale(minimumX as string & (Date | NumberValue)) - 24} dy='0.35em' textAnchor='end'>{tick.toLocaleString('nl-NL')}{dataSuffix}</text>
            <line x1={xScale(minimumX as string & (Date | NumberValue))} x2={width - margin.right} stroke={Colors.grey40} strokeWidth={1} strokeOpacity={0.5} />
        </g>
    ))

    return (
        <div style={{position: 'relative'}}>
            {dataIsEmpty &&
                <Card style={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', padding: 24}}>
                    Geen data gevonden in de geselecteerde periode.
                </Card>
            }
            <svg ref={svgRef} width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
                <g>
                    <g>{yTickElements}</g>
                    <g>{xTickElements}</g>
                </g>
                <g>
                    {series.flatMap((serie) => {
                        return (
                            <g className='stacked-bar' key={serie.date} data-date={serie.date}>
                                {serie.stacks.map((stack, i) => (
                                    <g key={`${serie.date}-${i}`}>
                                        {!currentStack &&
                                            <title>{stack.key} - {(stack.end - stack.start).toLocaleString('nl-NL')}</title>
                                        }
                                        <rect
                                            className='bar'
                                            data-key={stack.key}
                                            data-start={stack.start}
                                            data-end={stack.end}
                                            data-date={serie.date}
                                            x={interval === 'month' ?
                                                xScale(serie.date as string & (Date | NumberValue)) :
                                                xScale(new Date(serie.date) as string & (Date | NumberValue))}
                                            width={barWidth}
                                            fill={colorMap[stack.key] || '#D1D1D1'}
                                            stroke='white'
                                            strokeWidth={yScale(serie.total) > 1 ? 1 : 0}
                                        />
                                    </g>
                                ))}
                            </g>
                        )
                    })}
                </g>
            </svg>
            {/* Tooltip */}
            <div ref={tooltipRef} style={{maxWidth: 400, position: 'absolute', visibility: 'hidden', backgroundColor: 'white', padding: 12, boxShadow: '0 2px 4px rgba(0,0,0,0.2)', borderRadius: 4}}>
                <P style={{fontSize: 12, fontWeight: 700, marginBottom: 6}}>{moment(currentStack?.date).format(xTickFormat)}</P>
                {currentStack && (
                    <>
                        <ToolTipRow
                            key={currentStack.key}
                        >
                            {currentStack.key}: {currentStack.valueLabel || currentStack.value}{dataSuffix}
                        </ToolTipRow>
                        {tooltipHrefText && (
                            <P
                                style={{marginTop: 12, cursor: 'pointer'}}
                                onClick={() => {
                                    history.push(currentStack.href || '#')
                                }}
                            >
                                {tooltipHrefText}
                            </P>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}

const ToolTipRow = ({onClick, children}: {onClick?: () => void, children: ReactNode}) => {
    const [isHovering, setIsHovering] = useState(false)

    return (
        <P
            onClick={onClick}
            style={{fontSize: 12, margin: 0, cursor: onClick ? 'pointer' : 'default', backgroundColor: (isHovering && onClick) ? 'rgba(0, 0, 0, 0.02)' : 'transparent', padding: '2px 4px'}}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
        >
            {children}
        </P>
    )
}
